import { ApiEndpoints } from '@/types/routes';
import {
  IAuthApi,
  IConfirmResetPasswordBody,
  IConfirmResetPasswordRequest,
  IConfirmResetPasswordResponse,
  ILogInRequest,
  ILogInResponse,
  IResetPasswordRequest,
  IResetPasswordResponse,
  ISetOnboardingPasswordRequest,
  ISetOnboardingPasswordResponse,
} from '@/api/auth/auth.types';
import { IHttpClient } from '@/api/HttpClient';

export class AuthApi implements IAuthApi {
  private getBaseUrl(): string {
    return ApiEndpoints.AUTH_USER;
  }

  constructor(private readonly httpClient: IHttpClient) {}

  logInUser(logInCredentials: ILogInRequest): Promise<ILogInResponse> {
    return this.httpClient.post<ILogInRequest, ILogInResponse>(
      `${this.getBaseUrl()}token/`,
      logInCredentials,
      {
        withCredentials: true,
      },
    );
  }

  refreshToken(): Promise<ILogInResponse> {
    return this.httpClient.post<ILogInRequest, ILogInResponse>(
      `${this.getBaseUrl()}refresh/`,
      undefined,
      {
        withCredentials: true,
      },
    );
  }

  resetPassword(email: string): Promise<IResetPasswordResponse> {
    return this.httpClient.post<IResetPasswordRequest, IResetPasswordResponse>(
      `${this.getBaseUrl()}reset-password/`,
      { email: email },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  // whether the reset link can still set the password, without using it - a POST, the token stays out of urls
  checkResetPassword(token: string): Promise<IConfirmResetPasswordResponse> {
    return this.httpClient.post<{ token: string }, IConfirmResetPasswordResponse>(
      `${this.getBaseUrl()}reset-password/check/`,
      { token },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  confirmResetPassword(
    payload: IConfirmResetPasswordRequest,
  ): Promise<IConfirmResetPasswordResponse> {
    // the auth client has no case conversion (createAuthApiClient), so the body is in the API's snake_case here
    return this.httpClient.post<IConfirmResetPasswordBody, IConfirmResetPasswordResponse>(
      `${this.getBaseUrl()}reset-password/confirm/`,
      { token: payload.token, new_password: payload.newPassword },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  setOnboardingPassword(
    payload: ISetOnboardingPasswordRequest,
  ): Promise<ISetOnboardingPasswordResponse> {
    return this.httpClient.post<ISetOnboardingPasswordRequest, ISetOnboardingPasswordResponse>(
      `${this.getBaseUrl()}onboarding/set-password/`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }
}
